import { z } from "zod";
import {
  checkVerdictSchema,
  citationTypeSchema,
  confidenceLevelSchema,
  decisionSchema,
  detectionMethodSchema,
  evidenceTypeSchema,
  findingDispositionSchema,
  outputLanguageSchema,
  processingStatusSchema,
  reviewJobStatusSchema,
  reviewPrioritySchema,
  ruleOriginSchema,
  reviewProgressStageSchema,
  reviewStatusSchema,
  reviewTypeSchema,
  riskStatusSchema,
  routingOutcomeSchema,
  severitySchema,
} from "../enums.js";
import { normalizedRegionSchema } from "../ai.js";
import { isoDate, optionalIsoDate } from "./common.js";

export const materialRevisionSchema = z.object({
  id: z.string(),
  revision: z.number().int().positive(),
  channelId: z.string(),
  // Resolved for display so the browser never has to join against the channel
  // list to render a historical revision.
  channelName: z.string(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  sourceConfigurationCurrent: z.boolean(),
  bodyText: z.string().nullable(),
  vaultsageFileId: z.string().nullable(),
  fileName: z.string().nullable(),
  processingStatus: processingStatusSchema,
  createdAt: isoDate,
});
export type MaterialRevision = z.infer<typeof materialRevisionSchema>;

export const findingLocationModeSchema = z.enum(["DETECTED", "REVIEWER", "HIDDEN"]);
export type FindingLocationMode = z.infer<typeof findingLocationModeSchema>;

export const findingLocationSchema = z.object({
  /** One-based for PDFs; null for a standalone image. */
  pageNumber: z.number().int().positive().nullable(),
  region: normalizedRegionSchema,
});
export type FindingLocation = z.infer<typeof findingLocationSchema>;

/** Upper bound on boxes per finding, so one finding cannot flood the preview. */
export const MAX_FINDING_LOCATIONS = 20;

export const findingSchema = z.object({
  id: z.string(),
  title: z.string(),
  explanation: z.string(),
  severity: severitySchema,
  confidence: confidenceLevelSchema.nullable(),
  detectionMethod: detectionMethodSchema,
  evidenceQuote: z.string(),
  evidenceType: evidenceTypeSchema.optional(),
  suggestedRevision: z.string().nullable(),
  citationType: citationTypeSchema,
  citationTitle: z.string(),
  citationLocator: z.string().nullable(),
  pageNumber: z.number().int().positive().nullable().optional(),
  region: normalizedRegionSchema.nullable().optional(),
  /** Effective display mode. AI coordinates remain in pageNumber/region. */
  locationMode: findingLocationModeSchema.optional(),
  /** Reviewer-marked locations; non-empty only while locationMode is REVIEWER. */
  reviewerLocations: z.array(findingLocationSchema).optional(),
  /**
   * Where the evidence quote was found in the file's text layer or OCR.
   * Advisory: shown for a reviewer to accept, never treated as verified.
   */
  suggestedLocations: z.array(findingLocationSchema).optional(),
  locationReviewedByEmail: z.string().email().nullable().optional(),
  locationReviewedAt: optionalIsoDate,
  reviewerNote: z.string().nullable().optional(),
  disposition: findingDispositionSchema.nullable().optional(),
  dispositionByEmail: z.string().email().nullable().optional(),
  dispositionAt: optionalIsoDate,
});
export type Finding = z.infer<typeof findingSchema>;

/**
 * Something a reviewer caught that the analysis did not. The only source of
 * recall data the system has: a miss leaves no trace anywhere else, because by
 * definition no finding was created for it.
 */
export const missedFindingSchema = z.object({
  id: z.string(),
  description: z.string(),
  severity: severitySchema,
  /** Set when the reviewer could name the rule that should have caught it. */
  expectedRuleId: z.string().nullable(),
  capturedByEmail: z.string().email(),
  capturedAt: isoDate,
  /** Set once the miss has been turned into a taught rule via Educate Sage. */
  taughtRuleId: z.string().nullable(),
});
export type MissedFinding = z.infer<typeof missedFindingSchema>;

export const createMissedFindingSchema = z.object({
  description: z.string().min(1).max(2000),
  severity: severitySchema,
  expectedRuleId: z.string().min(1).nullable().optional(),
});
export type CreateMissedFindingInput = z.infer<typeof createMissedFindingSchema>;

export const decisionRecordSchema = z.object({
  id: z.string(),
  decision: decisionSchema,
  comment: z.string().nullable(),
  requiredCorrections: z.string().nullable(),
  decidedByEmail: z.string().email(),
  decidedAt: isoDate,
});
export type DecisionRecord = z.infer<typeof decisionRecordSchema>;

export const reviewSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  productName: z.string().nullable(),
  channelId: z.string(),
  channelName: z.string(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  status: reviewStatusSchema,
  reviewType: reviewTypeSchema.optional(),
  riskStatus: riskStatusSchema.nullable(),
  routingOutcome: routingOutcomeSchema.nullable().optional(),
  routingReason: z.string().nullable().optional(),
  priority: reviewPrioritySchema.optional(),
  assigneeEmail: z.string().email().nullable().optional(),
  dueAt: optionalIsoDate,
  requesterEmail: z.string().email(),
  requesterName: z.string().nullable(),
  requesterDepartment: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type ReviewSummary = z.infer<typeof reviewSummarySchema>;

/** One reference and what the analysis concluded about it. */
export const reviewChecklistItemSchema = z.object({
  referenceId: z.string(),
  title: z.string().nullable(),
  verdict: checkVerdictSchema,
  note: z.string().nullable(),
  citationType: citationTypeSchema,
});
export type ReviewChecklistItem = z.infer<typeof reviewChecklistItemSchema>;

/**
 * Why a result looks the way it does. Without this the UI can only show a
 * generic "analysis unavailable" message, leaving a requester with no idea
 * whether to retry, edit the material, or ask an administrator for help.
 */
export const analysisDiagnosticsSchema = z.object({
  aiSchemaValid: z.boolean(),
  rejectedFindingCount: z.number().int().nonnegative(),
  /** Approved organization rules and policies applied to this run. */
  approvedSourceCount: z.number().int().nonnegative(),
  /** Every reference applied, including product baseline references. */
  sourcesUsed: z.number().int().nonnegative(),
  /** True when only product baseline references were available. */
  baselineOnly: z.boolean(),
  /**
   * True when quality-control sampling pulled an otherwise automatic result
   * back to a person. Shown to the requester: without it, someone whose clean
   * material went to a reviewer anyway reads the system as inconsistent.
   */
  qcSampled: z.boolean().optional(),
  /** Operator-facing reason, safe to display. Null when the run was clean. */
  errorMessage: z.string().nullable(),
});
export type AnalysisDiagnostics = z.infer<typeof analysisDiagnosticsSchema>;

export const reviewDetailSchema = reviewSummarySchema.extend({
  campaignName: z.string().nullable().optional(),
  jurisdiction: z.string().nullable().optional(),
  targetAudience: z.string().nullable(),
  scheduledDate: optionalIsoDate,
  aiSummary: z.string().nullable(),
  materialOverview: z.string().nullable().optional(),
  recommendedAction: z.string().nullable().optional(),
  checklist: z.array(reviewChecklistItemSchema).optional(),
  diagnostics: analysisDiagnosticsSchema.nullable().optional(),
  currentRevision: materialRevisionSchema.nullable(),
  revisions: z.array(materialRevisionSchema),
  findings: z.array(findingSchema),
  missedFindings: z.array(missedFindingSchema).optional(),
  decision: decisionRecordSchema.nullable(),
  reportId: z.string().nullable(),
  /**
   * Whether the material can be downloaded with the ComplianceSage approval
   * page appended. Server-derived so the UI does not re-implement which
   * outcomes count as approved.
   */
  approvedDocumentAvailable: z.boolean(),
  // Set only for KNOWLEDGE_TEST reviews, when the tested lesson still exists:
  // lets the UI link back from a test result to the lesson it came from.
  lessonRuleId: z.string().nullable().optional(),
});
export type ReviewDetail = z.infer<typeof reviewDetailSchema>;

export const reviewProgressItemSchema = z.object({
  reviewId: z.string(),
  title: z.string(),
  reviewType: reviewTypeSchema,
  reviewStatus: reviewStatusSchema,
  jobStatus: reviewJobStatusSchema,
  progressStage: reviewProgressStageSchema,
  processingStatus: processingStatusSchema,
  channelName: z.string(),
  categoryName: z.string().nullable(),
  categoryParentName: z.string().nullable(),
  sourceCounts: z.object({
    rules: z.number().int().nonnegative(),
    policies: z.number().int().nonnegative(),
    dictionaryEntries: z.number().int().nonnegative(),
  }),
  sources: z.array(
    z.object({
      referenceId: z.string(),
      citationType: z.enum(["RULE", "POLICY", "DICTIONARY"]),
      title: z.string(),
      verdict: checkVerdictSchema.nullable(),
      // Only set when citationType is RULE — distinguishes Custom Rules from
      // Educate Sage (taught) rules for the Sage Workflow live view.
      origin: ruleOriginSchema.nullable().optional(),
    }),
  ),
  attempts: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  analysisStartedAt: optionalIsoDate,
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type ReviewProgressItem = z.infer<typeof reviewProgressItemSchema>;

/**
 * Historical total-duration benchmark for a completed analysis run, used to
 * render an approximate "time remaining" while a review is still analyzing.
 * `p50DurationMs` is the median wall-clock time (queue wait + analysis) across
 * the organization's most recently completed runs; `sampleSize` lets the
 * client fall back to a plain elapsed-time display when there isn't enough
 * history yet to trust the estimate.
 */
export const reviewProgressEstimateSchema = z.object({
  p50DurationMs: z.number().int().nonnegative().nullable(),
  sampleSize: z.number().int().nonnegative(),
});
export type ReviewProgressEstimate = z.infer<typeof reviewProgressEstimateSchema>;

export const createReviewSchema = z.object({
  title: z.string().min(1).max(300),
  productName: z.string().max(300).optional(),
  campaignName: z.string().max(300).optional(),
  jurisdiction: z.string().max(100).optional(),
  channelId: z.string().min(1),
  categoryId: z.string().min(1).optional(),
  targetAudience: z.string().max(300).optional(),
  scheduledDate: isoDate.optional(),
  bodyText: z.string().max(100000).optional(),
  /** Requester's UI language; the AI writes its analysis prose in it. */
  outputLanguage: outputLanguageSchema.optional(),
});
export type CreateReviewInput = z.infer<typeof createReviewSchema>;

export const listReviewsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: reviewStatusSchema.optional(),
  riskStatus: riskStatusSchema.optional(),
  channelId: z.string().optional(),
  priority: reviewPrioritySchema.optional(),
  assigneeEmail: z.string().email().optional(),
  requesterEmail: z.string().email().optional(),
  search: z.string().max(200).optional(),
});
export type ListReviewsQuery = z.input<typeof listReviewsQuerySchema>;

export const reviewListPageSchema = z.object({
  items: z.array(reviewSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ReviewListPage = z.infer<typeof reviewListPageSchema>;

export const reviewMetricsSchema = z.object({
  total: z.number().int(),
  awaitingDecision: z.number().int(),
  highRisk: z.number().int(),
  selfCheckClear: z.number().int(),
  selfCheckRejected: z.number().int(),
  overdue: z.number().int(),
  unassigned: z.number().int(),
  escalationRate: z.number(),
  overrideRate: z.number(),
});
export type ReviewMetrics = z.infer<typeof reviewMetricsSchema>;

export const assignReviewSchema = z.object({
  assigneeEmail: z.string().email().nullable(),
  priority: reviewPrioritySchema.optional(),
  dueAt: isoDate.nullable().optional(),
});
export type AssignReviewInput = z.infer<typeof assignReviewSchema>;

/**
 * A reviewer's verdict on one finding. Both fields are independently optional
 * so the two controls that write them — the note box and the disposition
 * buttons — can each patch without clobbering the other; an absent key means
 * "leave unchanged", which is why `null` has to stay a distinct, meaningful
 * value (it clears).
 */
export const updateFindingReviewSchema = z
  .object({
    reviewerNote: z.string().max(2000).nullable().optional(),
    disposition: findingDispositionSchema.nullable().optional(),
    locationUpdate: z
      .discriminatedUnion("action", [
        z.object({
          action: z.literal("SET"),
          locations: z.array(findingLocationSchema).min(1).max(MAX_FINDING_LOCATIONS),
        }),
        z.object({ action: z.literal("HIDE") }),
        z.object({ action: z.literal("RESET") }),
      ])
      .optional(),
  })
  .refine(
    (value) =>
      value.reviewerNote !== undefined ||
      value.disposition !== undefined ||
      value.locationUpdate !== undefined,
    {
      message: "Provide a reviewer note, disposition, or location update.",
    },
  );
export type UpdateFindingReviewInput = z.infer<typeof updateFindingReviewSchema>;

export const recordDecisionSchema = z
  .object({
    decision: decisionSchema,
    comment: z.string().max(4000).optional(),
    requiredCorrections: z.string().max(4000).optional(),
    /**
     * What the analysis failed to catch. Recorded with the decision because
     * that is the only moment someone knows both what the AI said and what was
     * actually wrong; asked for later, nobody remembers.
     */
    missedFindings: z.array(createMissedFindingSchema).max(20).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.decision !== "APPROVED" && !value.requiredCorrections?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["requiredCorrections"],
        message: "Required corrections are mandatory for conditional approval or rejection.",
      });
    }
  });
export type RecordDecisionInput = z.infer<typeof recordDecisionSchema>;
